import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import produce from "immer";
import filter from "lodash/filter";
import find from "lodash/find";
import map from "lodash/map";
import toLower from "lodash/toLower";
import DynamicSprite from "components/games/elements/GenericGame/DefaultLeaderboard/DynamicSprite";
import GenericGameLeaderboard from "components/games/elements/GenericGame/GenericGameLeaderboard";
import Modal from "components/common/Modal";
import DefaultPodium from "./DefaultPodium";

export default function DefaultLeaderboard(props) {
    const {
        animate = false,
        animationIndexes = [],
        animationOptions = [],
        backgroundImage = "",
        backgroundImageAlt = "A background image needs to be selected",
        currentClinicId = undefined,
        currentEvent = undefined,
        data = [],
        eventConfig = {},
        gameType = null,
        isVertical = false,
        podium=undefined,
        printing = false,
	    progressBarColors = [],
        randomAnimation = false,
        rowStyle = {},
        rowsContainerStyle = {},
        scores = [],
        showAllPoints = false,
        showConfetti = false,
        showRanking = false,
        snapshot = false,
    } = props;
    const [showPodium, setShowPodium] = useState(false);
    const [spritesLoaded, setSpritesLoaded] = useState(false);
    const clinicData = find(data, { ClinicId: currentClinicId });

    const config = useMemo(() => {
        return eventConfig?.[currentEvent?.EventId] || {
            background: backgroundImage,
            backgroundAlt: backgroundImageAlt,
            rowsContainerStyle: rowsContainerStyle,
            rowStyle: rowStyle,
            scoresStyle: {},
        };
    }, [currentEvent]);

    const getAltText = (row) => {
        return row.clinicAlias;
    };

    const getIsTransparent = (row) => {
        let rowWidthPercentage = 50 + row.PercentGrowth / 2;
        if (rowWidthPercentage >= 100) {
          rowWidthPercentage = 100;
        }
        return rowWidthPercentage < 60;
    };

    const getImage = (index, id, imageFile=null) => {
        return (
            <DynamicSprite
                gameType={gameType}
                imageFile={imageFile}
                imageId={id}
                index={index}
                onLoad={() => setSpritesLoaded(true)}
            />
        );
    };

    const getFrames = (animation) => {
        let framesList = [];
        for (let i = 0; i < animation.frameCount; i++) {
            const row = Math.floor(i / animation.frameColumns);
            const frameRows = Math.ceil(animation.frameCount / animation.frameColumns);
            framesList.push({
                height: `${(animation.imageHeight / 16)}em`,
                left: `-${(((animation.imageWidth / animation.frameColumns) * i) % animation.imageWidth) / 16}em`,
                top: `-${(row * (animation.imageHeight / frameRows)) / 16}em`,
                width: `${(animation.imageWidth / 16)}em`,
            });
        }
        return framesList;
    };

    const options = useMemo(() => {
        return produce(animationOptions, (draft) => {
            map(animationOptions, (o, o_index) => {
                map(o.animations, (a, a_index) => {
                    draft[o_index].animations[a_index].getAltText = (row) => getAltText(row);
                    draft[o_index].animations[a_index].getIsTransparent = (row) => getIsTransparent(row);
                    draft[o_index].animations[a_index].getImage = (index) => getImage(index, o.id, animationOptions[o_index].animations[a_index]?.imageFile);
                    draft[o_index].animations[a_index].frames = getFrames(a);
                    const frameRows = Math.ceil(a.frameCount / a.frameColumns);
                    draft[o_index].animations[a_index].height = `${(a.imageHeight / frameRows) / 16}em`;
                    draft[o_index].animations[a_index].width = `${(a.imageWidth / a.frameColumns) / 16}em`;
                });
            });
        });
    }, [animationOptions]);

    const handleGetRowAnimationIndex = (row) => {
        if (randomAnimation) {
            const characterAnimations = filter(animationIndexes, { avatarType: row.AvatarType });
            const remainder = (row.Place - 1) % characterAnimations?.length;
            return characterAnimations?.[remainder]?.animationIndex || characterAnimations[0];
        } else if (currentEvent) {
            const currentAnimation = find(animationIndexes, {
                avatarType: toLower(row.AvatarType) || toLower(row.avatarType),
                eventId: currentEvent.eventId || currentEvent.EventId,
            });
            return currentAnimation?.animationIndex || 0;
        } else {
            return 0;
        }
    };

    const handleAnimationComplete = () => {
        if (!!clinicData && !!podium?.animationOptions?.length) {
            if (!snapshot && !showPodium && currentEvent?.DaysRemaining < 1) {
                setShowPodium(true);
            }
        }
    }

    return (
        <>
            <GenericGameLeaderboard
                animate={animate}
                animationOptions={options}
                backgroundImage={config.background || backgroundImage}
                backgroundImageAlt={config.backgroundAlt || backgroundImageAlt}
                currentClinicId={currentClinicId}
                data={data}
                getRowAnimationIndex={handleGetRowAnimationIndex}
                goal={null}
                isVertical={isVertical}
                onAnimationComplete={handleAnimationComplete}
                points
                printing={printing}
                progressBarColors={progressBarColors}
                rowStyle={config.rowStyle}
                rowsContainerStyle={config.rowsContainerStyle || rowsContainerStyle}
                showAllPoints={showAllPoints}
                showConfetti={showConfetti && !snapshot && currentEvent?.DaysRemaining < 1}
                showRanking={showRanking}
                showVLSD={false}
                scores={scores}
                scoresStyle={config.scoresStyle}
                spritesLoaded={spritesLoaded}
            />
            {showPodium && (
                <Modal
                    show={showPodium}
                    mediumSmall
                    onClose={() => setShowPodium(false)}
                >
                    <DefaultPodium
                        clinicData={clinicData}
                        config={podium}
                    />
                </Modal>
            )}
        </>
    );
}

DefaultLeaderboard.propTypes = {
    animate: PropTypes.bool,
    currentClinicId: PropTypes.number,
    data: PropTypes.array,
    currentEvent: PropTypes.shape({
        DaysRemaining: PropTypes.number,
        EventId: PropTypes.number,
    }),
    podium: PropTypes.object,
    printing: PropTypes.bool,
    showAllPoints: PropTypes.bool,
    snapshot: PropTypes.bool,
};
